import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  LinearProgress,
  Theme,
  ThemeProvider,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import {
  DataGridPro,
  GridCellParams,
  GridColDef,
  GridSlots,
  GridRowHeightParams,
} from '@mui/x-data-grid-pro';
import { Refresh } from '@mui/icons-material';
import { brokerSharingTheme } from '#app/theme';
import { publishFSCustomEvent } from '#app/fullstory';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomNoRowsOverlay from './CustomNoRows';
import {
  CellComponent,
  ChipsCellComponent,
  CustomToolbar,
} from './TableComponents';
import { formatDistanceToNow } from 'date-fns';
import { BROKER_NAMES } from './utils';

// Order of columns in the data grid
const order = [
  'mcNumber',
  'carrierName',
  'carrierHomeCity',
  'carrierHomeState',
  'carrierPrimaryEquipment',
  'carrierOwner',
  'searchDate',
  'equipmentSearched',
  'pickupDate',
  'pickupCity',
  'pickupState',
  'dropOffCity',
  'dropOffState',
  'deadheadMiles',
  'maxWeight',
  'carrierEmail',
  'carrierPhone',
  'carrierAuthorityAge',
  'rateConUploaded',
];

// Slots for the data grid
const DATA_GRID_SLOTS: Partial<GridSlots> = {
  toolbar: CustomToolbar,
  loadingOverlay: LinearProgress as GridSlots['loadingOverlay'],
  noRowsOverlay: CustomNoRowsOverlay,
};

/* STYLES */
export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    wrapper: {
      display: 'flex',
      justifyContent: 'flex-start',
    },
    blurredText: {
      filter: 'blur(5px)',
    },
    link: {
      color: 'black',
      wordWrap: 'break-word',
    },
  })
);

interface Props {}
/* MAIN COMPONENT */
function BrokerSharing(props: Props) {
  /* HOOKS */
  const navigate = useNavigate();
  const classes = useStyles();
  const location = useLocation();
  const url = location.pathname;
  const params = new URLSearchParams(location.search);
  const brokerName = params.get('broker');

  /* STATE */
  // const label = { inputProps: { 'aria-label': 'Auto Refresh' } };
  // const [autoRefresh, setAutoRefresh] = React.useState(false);
  // const [timerCount, setTimerCount] = React.useState(30);
  const [loading, setLoading] = React.useState(false);
  const [rawData, setRawData] = React.useState<any[]>([]);
  const [daysOfData] = React.useState('3');
  const [showOnlyLastSearch, setShowOnlyLastSearch] = React.useState(false);

  /* FUNCTIONS */
  /**
   * Formats data and sets it
   */
  const rows = React.useMemo(() => {
    // Format data for rows
    const formattedData = rawData.map((item: any, index: number) => {
      const obj: { [key: string]: any } = {};
      for (let key in item) {
        obj[key] = item[key].value;
      }
      obj.id = index;

      // searchDate should be formatted as MM/DD/YYYY in EST
      const searchDate = new Date(obj.searchDate);
      const offset = searchDate.getTimezoneOffset();
      searchDate.setMinutes(searchDate.getMinutes() - offset);

      // Extract the date parts
      let year = searchDate.getUTCFullYear();
      let month = searchDate.getUTCMonth() + 1; // Months are 0-based in JavaScript
      let day = searchDate.getUTCDate();

      // Format the date
      obj.searchDate = `${('0' + month).slice(-2)}/${('0' + day).slice(
        -2
      )}/${year}`;

      // Take only the date part for pickupDate as a string
      // Extract the date parts from the string
      [year, month, day] = obj.pickupDate.split('T')[0].split('-').map(Number);

      // Format the date
      const formattedDate = `${('0' + month).slice(-2)}/${('0' + day).slice(
        -2
      )}/${year}`;
      obj.pickupDate = formattedDate;

      obj.carrierAuthorityAge =
        obj.carrierAuthorityAge && formatDistanceToNow(obj.carrierAuthorityAge);
      obj.deadheadMiles = Number(obj.deadheadMiles).toLocaleString();
      obj.maxWeight = obj.maxWeight
        ? Number(obj.maxWeight).toLocaleString()
        : '';
      return obj;
    });

    return formattedData;
  }, [rawData]);

  const filteredRows = React.useMemo(() => {
    // Filter only the last search for each carrier email
    if (showOnlyLastSearch) {
      const seen = new Set();
      return rows.filter((item: any) => {
        if (seen.has(item.carrierEmail)) {
          return false;
        } else {
          seen.add(item.carrierEmail);
          return true;
        }
      });
    }

    return rows;
  }, [rows, showOnlyLastSearch]);

  const columns = React.useMemo(() => {
    // Get column data from the first object in the array
    const dataObj = rawData[0];
    // If there is no data, return an empty array
    if (!dataObj) {
      return [];
    }

    // Format column data for columns
    const formattedColumnData: GridColDef[] = Object.keys(dataObj).map(
      (key) => {
        const obj: GridColDef = {
          field: key,
          headerName: dataObj[key].header,
          headerClassName: 'super-app-theme--header',
          cellClassName: 'super-app-theme--cell',
          // Set default width
          width: 150,
          // Set default value
          valueGetter: (value) => value || '-',
        };
        if (key === 'carrierEmail') {
          obj.width = 260;
          obj.renderCell = (params) => (
            <CellComponent value={params.value} type="email" />
          );
        } else if (key === 'carrierPhone') {
          obj.renderCell = (params) => (
            <CellComponent value={params.value} type="phone" />
          );
        } else if (key === 'equipmentSearched') {
          obj.width = 300;
          obj.renderCell = (params) => {
            return <ChipsCellComponent value={params.value} />;
          };
        } else if (key === 'carrierPrimaryEquipment') {
          obj.renderCell = (params) => {
            return <ChipsCellComponent value={params.value} />;
          };
        } else if (key === 'rateConUploaded') {
          obj.renderCell = (params) => {
            return params.value === true ? '✅' : '-';
          };
        } else if (
          key === 'searchDate' ||
          key === 'pickupDate' ||
          key === 'mcNumber' ||
          key === 'carrierHomeState' ||
          key === 'pickupState' ||
          key === 'dropOffState'
        ) {
          obj.width = 120;
        } else if (key === 'carrierName') {
          obj.width = 200;
        }

        if (key === 'dropOffCity' || key === 'dropOffState') {
          obj.valueGetter = (value, row) => {
            if (row.dropOffCity === null && row.dropOffState === null) {
              return 'Anywhere';
            }
            return value || '-';
          };
        }

        return obj;
      }
    );

    // Sort the columns based on the order array defined above
    formattedColumnData.sort((a, b) => {
      const indexA = order.indexOf(a.field);
      const indexB = order.indexOf(b.field);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB; // normal sorting
    });

    return formattedColumnData;
  }, [rawData]);

  /**
   * Fetches carrier data
   */
  const fetchCarrierData = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/broker_sharing/carriers?days=${daysOfData}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();
      const dataArray = data?.data;
      if (!dataArray) {
        console.error('Error fetching carrier data');
        return;
      }

      // Set raw data
      setRawData(dataArray);
    } catch (error) {
      console.error('Error fetching carrier data:', error);
    } finally {
      setLoading(false);
    }
  }, [daysOfData]);

  /**
   * Handle switch change event
   */
  // const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   setAutoRefresh(event.target.checked);
  //   setTimerCount(30);
  // };

  /**
   * Handle checkbox change event
   */
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowOnlyLastSearch(event.target.checked);
  };

  /**
   * Handle cell click event for phone and email
   */
  const onCellClick = (cell: GridCellParams) => {
    if (cell.field !== 'carrierPhone' && cell.field !== 'carrierEmail') {
      return;
    }

    const eventName =
      cell.field === 'carrierPhone'
        ? 'CarrierPhoneClicked'
        : 'CarrierEmailClicked';

    const eventProperties = {
      ...cell.row,
      url,
      broker: brokerName,
    };

    // Send FullStory event when phone or email is clicked
    publishFSCustomEvent(eventName, eventProperties);
  };

  const getRowHeight = React.useCallback(({ model }: GridRowHeightParams) => {
    // if (model.equipmentSearched?.length > 3) {
    //   return 86;
    // }

    // return 50;
    // Fixes link wrapping issue
    return 'auto';
  }, []);

  /**
   * Handle refresh
   */
  const handleRefresh = React.useCallback(() => {
    fetchCarrierData();
  }, [fetchCarrierData]);

  /* EFFECTS */
  // Fetch carrier data on mount
  React.useEffect(() => {
    fetchCarrierData();
  }, [fetchCarrierData]);

  // Set document title on mount
  React.useEffect(() => {
    document.title = 'Real-Time Carrier Data | TrueNorth';
  }, []);

  // Auto refresh every 30 seconds if enabled
  // React.useEffect(() => {
  //   if (autoRefresh) {
  //     const timer = setInterval(() => {
  //       setTimerCount((prev) => {
  //         if (prev === 1) {
  //           handleRefresh();
  //           return 30;
  //         }
  //         return prev - 1;
  //       });
  //     }, 1000);
  //     return () => clearInterval(timer);
  //   }
  // }, [autoRefresh, handleRefresh]);

  // Redirect to 404 page if broker name is not provided in the URL
  // or doesn't match the approved broker names
  React.useEffect(() => {
    if (!brokerName || !BROKER_NAMES.includes(brokerName)) {
      navigate('/registration/404');
    }
  }, [brokerName, navigate]);

  /* RENDER */
  return (
    <ThemeProvider theme={brokerSharingTheme}>
      <Box
        className={classes.wrapper}
        display="flex"
        px={10}
        py={2}
        flexDirection={'column'}>
        <Box display={'flex'} flexDirection={'row'}>
          <img src={require('#app/icons/TNLogo.svg').default} alt="TNLogo" />
          <h3>Real-Time Carrier Data</h3>
        </Box>
        <Alert severity="info" sx={{ borderRadius: 2, marginY: 2 }}>
          Presenting carrier data for the past 3 days. Our data is continuously
          updated in real-time. Each entry corresponds to a carrier searching
          for a load through the TrueNorth app.
        </Alert>
        <Box style={{ width: '100%' }}>
          <Box pb={4} pt={2}>
            <Box
              sx={{
                flexDirection: 'row',
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
              gap={4}>
              <Button
                variant="outlined"
                sx={{ borderRadius: 2, height: 40, px: 2 }}
                startIcon={<Refresh />}
                onClick={handleRefresh}>
                Refresh
              </Button>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      value={showOnlyLastSearch}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label="Show Recent Searches Only"
                />
              </FormGroup>
              {/* <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          {...label}
                          checked={autoRefresh}
                          onChange={handleSwitchChange}
                        />
                      }
                      label="Auto Refresh (every 30 seconds)"
                      labelPlacement="start"
                    />
                  </FormGroup> */}
              {/* <RowRadioButtonsGroup
                    value={daysOfData}
                    setValue={setDaysOfData}
                  /> */}
            </Box>
            {/* {autoRefresh && (
                  <Alert
                    icon={<Refresh fontSize="inherit" />}
                    severity="info"
                    sx={{ marginTop: 4, borderRadius: 2 }}>
                    {`Refreshing in ${timerCount} seconds`}
                  </Alert>
                )} */}
          </Box>
          <Box
            sx={{
              height: 1000,
              marginBottom: 10,
              width: '100%',
              '& .super-app-theme--header': {
                color: 'gray',
              },
              '& .super-app-theme--cell': {
                alignContent: 'center',
              },
            }}>
            <DataGridPro
              getRowHeight={getRowHeight}
              onCellClick={onCellClick}
              sx={{
                borderRadius: 2,
                '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
                  py: '8px',
                },
                '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
                  py: '10px',
                },
                '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {
                  py: '22px',
                },
              }}
              rows={filteredRows}
              columns={columns}
              slots={DATA_GRID_SLOTS}
              loading={loading}
              pagination={true}
            />
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default BrokerSharing;
